import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import { useUserNew } from "src/api/client-api/useUserNew";
import { Loading } from "src/components/utils/Loading";
import { CompaniesService } from "src/services/Companies/CompaniesService";
import { URLHandler } from "src/services/URLHandler";
import { buildLogoutUrl, markSessionEstablished } from "src/services/loginEntry";
import { useSsoReturnDestination } from "src/hooks/useSsoReturnDestination";
import { registerMonitoringIdentity } from "src/utils/monitoring";
import { UserContext, UserInitialValues } from "./context";
import { getSelectableCompanies } from "../permissionMatching";
import { requiresCompanyContext } from "../companyContextRouting";

type UserProviderProps = {
    children: ReactNode;
};
export const UserProvider = (props: UserProviderProps) => {
    const { children } = props;
    const { user, isReady, isError } = useUserNew();
    const location = useLocation();
    useSsoReturnDestination(user !== undefined);
    const authRoutes = ["/oauth", "/cas3", "/saml2"];

    const isOnAuthRoute = authRoutes.some((route) => location.pathname.startsWith(route));

    useEffect(() => {
        if (user !== undefined) {
            markSessionEstablished();
        }
    }, [user]);

    useEffect(() => {
        if (user === undefined) {
            registerMonitoringIdentity(undefined);
            return;
        }

        const selectableCompanies = getSelectableCompanies(user);
        const currentCompany = CompaniesService.selectCurrentCompany(selectableCompanies);

        registerMonitoringIdentity({
            user,
            company: currentCompany ?? selectableCompanies[0],
        });
    }, [user]);

    useEffect(() => {
        return () => {
            registerMonitoringIdentity(undefined);
        };
    }, []);

    if (isReady === false) {
        return <Loading />;
    }

    if (isError && isOnAuthRoute) {
        // We should NOT throw logout on `isError`.
        // There is a global Retry system on specific `401: Unauthorized` that will
        // Reset the token.
        // I am leaving the system to say...
        // If there is an error here, AND we are the URLs about :
        // oauth | cas3 | saml2
        // Then, I allow a logout.
        // But global logout is already handled.
        const url = buildLogoutUrl({ reason: "expired" });
        return <Navigate to={url} replace />;
    }

    if (user === undefined) {
        return <Loading />;
    }

    const selectableCompanies = getSelectableCompanies(user);
    const currentCompany = CompaniesService.selectCurrentCompany(selectableCompanies);
    if (selectableCompanies.length === 0 && requiresCompanyContext(location.pathname)) {
        const url = URLHandler.login.noCompany();
        return <Navigate to={url} replace />;
    }

    const selectedCompany = currentCompany ?? selectableCompanies[0] ?? null;

    const items: UserInitialValues = {
        user,
        selectedCompany,
    };

    return <UserContext.Provider value={items}>{children}</UserContext.Provider>;
};
